import { useTranslation } from 'react-i18next';
import { Card, CardTitle, PlatformIcon } from '@/components/ui';
import { getShortUrl } from '@/utils/domainResolver';
import type { Deeplink } from '@/types';

interface TopLinksTableProps {
  links: { linkId: string; clicks: number; conversions?: number; revenue?: number; deeplink?: Deeplink }[];
  title?: string;
}

const TopLinksTable = ({ links, title }: TopLinksTableProps) => {
  const { t, i18n } = useTranslation();
  const displayTitle = title ?? t('stats.topPerformingLinks');

  if (links.length === 0) {
    return (
      <Card variant="default" padding="lg">
        <CardTitle className="mb-6">{displayTitle}</CardTitle>
        <div className="py-8 text-center text-gray-400">
          {t('common.noData')}
        </div>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none">
      <div className="p-6 border-b border-gray-100 dark:border-gray-700">
        <CardTitle>{displayTitle}</CardTitle>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {links.map((item, index) => {
          return (
            <div
              key={item.linkId}
              className="flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              {/* Rank */}
              <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center font-medium text-dark-800 dark:text-white">
                {index + 1}
              </div>

              {/* Platform Icon */}
              {item.deeplink && (
                <PlatformIcon platform={item.deeplink.platform} size="md" />
              )}

              {/* Link Info */}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-dark-800 dark:text-white truncate">
                  {item.deeplink?.name || (item.deeplink ? getShortUrl(item.deeplink) : `klipr.it/${item.linkId}`)}
                </p>
                {item.deeplink && (
                  <p className="text-sm text-gray-500 truncate">
                    {item.deeplink.originalUrl}
                  </p>
                )}
              </div>

              {/* Clicks */}
              <div className="text-right">
                <p className="font-bold text-dark-800 dark:text-white">{item.clicks.toLocaleString(i18n.language)}</p>
                <p className="text-xs text-gray-400">{t('common.clicks')}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default TopLinksTable;
